/**
 * Composite risk score — the canonical query-relative derivation (plan §6.5).
 *
 * The score is min-max normalised over the PASSED ROW SET, so the same ticker
 * scores differently under `ALL` than under `DJI`. That is why it is never
 * stored and never cached per ticker: a stored value would have been normalised
 * against some universe and would then be served under a different filter.
 *
 * ADR-0004: the return is a `ReadonlyMap`, not a `_risk` field written onto the
 * rows. The demo's `rows.forEach(t => { t._risk = … })` mutates the shared
 * universe objects, so the last screen to render decides what every other screen
 * reads. The map makes the score's lifetime explicit and the input immutable.
 */
import type { Ticker, WindowKey } from '@meridian/contracts';

import { RISK_WEIGHT_BETA, RISK_WEIGHT_DD, RISK_WEIGHT_VOL } from '../constants/risk';

export interface RiskInput {
  readonly ticker: Ticker;
  readonly vol: number | null;
  readonly beta: number | null;
  readonly dd: Readonly<Record<WindowKey, number | null>>;
}

const RISK_SCALE = 100;
/** Beta risk is distance from the market, in either direction. */
const BETA_NEUTRAL = 1;

interface ScorableRow {
  readonly ticker: Ticker;
  readonly vol: number;
  readonly drawdown: number;
  readonly betaDistance: number;
}

interface Domain {
  readonly lo: number;
  readonly hi: number;
}

/**
 * Min-max domain once per dimension (F8: hi <= lo → 0). Avoids the O(n²)
 * rescans of calling `nz(x, values)` per row.
 */
function domainOf(values: readonly number[]): Domain | null {
  const first = values[0];
  if (first === undefined) return null;
  const rest = values.slice(1);
  return {
    lo: rest.reduce((a, v) => (v < a ? v : a), first),
    hi: rest.reduce((a, v) => (v > a ? v : a), first),
  };
}

function nzDomain(x: number, domain: Domain | null): number {
  if (domain === null || !(domain.hi > domain.lo)) return 0;
  return (x - domain.lo) / (domain.hi - domain.lo);
}

/**
 * A row missing any input is OMITTED, never scored as zero (R2b). Scoring a
 * null drawdown as 0 would put that row at the safest end of the normalised
 * range — the single most flattering position available — purely because the
 * datum is absent. Its exclusion also keeps it out of the min/max domain, so
 * the remaining rows are normalised against real observations only.
 */
function toScorable(row: RiskInput, window: WindowKey): ScorableRow | null {
  const drawdown = row.dd[window];
  if (row.vol === null || row.beta === null || drawdown === null) return null;
  return {
    ticker: row.ticker,
    vol: row.vol,
    drawdown: -drawdown,
    betaDistance: Math.abs(row.beta - BETA_NEUTRAL),
  };
}

export function computeRiskScores(
  rows: readonly RiskInput[],
  window: WindowKey,
): ReadonlyMap<Ticker, number> {
  const scorable = rows
    .map((r) => toScorable(r, window))
    .filter((r): r is ScorableRow => r !== null);
  const volDomain = domainOf(scorable.map((r) => r.vol));
  const ddDomain = domainOf(scorable.map((r) => r.drawdown));
  const betaDomain = domainOf(scorable.map((r) => r.betaDistance));
  return new Map(
    scorable.map((r) => [
      r.ticker,
      RISK_SCALE *
        (RISK_WEIGHT_VOL * nzDomain(r.vol, volDomain) +
          RISK_WEIGHT_DD * nzDomain(r.drawdown, ddDomain) +
          RISK_WEIGHT_BETA * nzDomain(r.betaDistance, betaDomain)),
    ]),
  );
}
